import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { loginInputSchema, type LoginInput } from '@app/shared';
import { apiErrorMessage, ApiRequestError } from '@/lib/api';
import { useLogin } from '@/features/auth/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const login = useLogin();
  const [totpRequired, setTotpRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: '', password: '', totpCode: undefined },
  });

  const onSubmit = handleSubmit((data) => {
    login.mutate(data, {
      onSuccess: () => {
        void navigate({ to: search.redirect ?? '/biens' });
      },
      onError: (error) => {
        if (error instanceof ApiRequestError && error.code === 'totp_required') setTotpRequired(true);
      },
    });
  });

  const isTotpPrompt = login.error instanceof ApiRequestError && login.error.code === 'totp_required';
  const errorMessage =
    login.error && !isTotpPrompt
      ? apiErrorMessage(login.error, 'Connexion impossible. Vérifiez le réseau et réessayez.')
      : null;

  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-col items-center text-center">
          <img src="/logo.webp" alt="Evasion-Soleil" className="mb-1 size-12 rounded-full" />
          <CardTitle className="font-serif text-xl text-primary">Evasion-Soleil</CardTitle>
          <p className="text-sm text-muted-foreground">Gestion immobilière</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Adresse e-mail</Label>
              <Input id="email" type="email" autoComplete="username" {...register('email')} />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
            </div>

            {totpRequired && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="totpCode">Code de double authentification</Label>
                <Input
                  id="totpCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  {...register('totpCode')}
                />
                {errors.totpCode && <p className="text-sm text-destructive">{errors.totpCode.message}</p>}
              </div>
            )}

            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={login.isPending} className="mt-2">
              {login.isPending ? 'Connexion…' : 'Se connecter'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
